<template>
  <div class="mt-4 rounded-md bg-white px-4 pb-3 pt-2">
    <div class="grid gap-4 md:grid-cols-2 md:gap-12">
      <div class="space-y-2">
        <h3>
          {{ t('start') }}
        </h3>
        <VehicleStatus
          v-if="showStart"
          v-model:milage="milageStart"
          v-model:fuel-level="fuelLevelStart"
          :fuel-type="fuelType"
        />
        <p v-else class="py-2">{{ t('notHandedOver') }}</p>
      </div>
      <div class="space-y-2">
        <h3>
          {{ t('end') }}
        </h3>
        <VehicleStatus
          v-if="showEnd"
          v-model:milage="milageEnd"
          v-model:fuel-level="fuelLevelEnd"
          :fuel-type="fuelType"
        />
        <p v-else class="py-2">{{ t('notReturned') }}</p>
      </div>
    </div>
    <div class="mt-6 flex justify-end gap-4">
      <CVButton :is-loading="isSaving" variant="default" @click.prevent="handleSave">{{
        t('save')
      }}</CVButton>
      <CVButton variant="secondary" @click.prevent="handleCancel">{{ t('cancel') }}</CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useForm } from 'vee-validate';
import VehicleStatus from './VehicleStatus.vue';
import { computed, ref, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import CVButton from '@/components/buttons/CVButton.vue';
import type { Allocation } from '@/entities/allocations/allocation.entity';
import { AllocationStatus, type UpdateAllocationDto } from '@carvia/ros-client-types';

const props = defineProps<{
  allocation: Allocation;
  isSaving: boolean;
}>();

const emit = defineEmits<{
  (e: 'save', allocationId: string, dto: UpdateAllocationDto): void;
  (e: 'cancel'): void;
}>();

const { t } = useI18n();

const showStart = computed(
  () =>
    props.allocation.status === AllocationStatus.HANDED_OVER ||
    props.allocation.status === AllocationStatus.RETURNED,
);

const showEnd = computed(() => props.allocation.status === AllocationStatus.RETURNED);

const fuelType = computed(
  () => props.allocation.car.fuelType ?? props.allocation.car.vehicleType.fuelType,
);

const { handleSubmit, setValues } = useForm({
  initialValues: {
    milageStart: props.allocation.milageStart,
    milageEnd: props.allocation.milageEnd,
    fuelLevelStart: props.allocation.fuelLevelStart,
    fuelLevelEnd: props.allocation.fuelLevelEnd,
  },
});

const milageStart = ref(props.allocation.milageStart);
const milageEnd = ref(props.allocation.milageEnd);
const fuelLevelStart = ref(props.allocation.fuelLevelStart);
const fuelLevelEnd = ref(props.allocation.fuelLevelEnd);

watchEffect(() => {
  setValues({
    milageStart: milageStart.value,
    milageEnd: milageEnd.value,
    fuelLevelStart: fuelLevelStart.value,
    fuelLevelEnd: fuelLevelEnd.value,
  });
});

const handleSave = handleSubmit((values) => {
  emit('save', props.allocation.id, values);
});

const handleCancel = () => {
  emit('cancel');
};
</script>

<i18n lang="json">
{
  "en": {
    "start": "Start",
    "end": "End",
    "notHandedOver": "can only be edited once the car has been handed over",
    "notReturned": "can only be edited once the car has been returned"
  },
  "de": {
    "start": "Start",
    "end": "Ende",
    "notHandedOver": "Kann erst bearbeitet werden, sobald das Auto übergeben wurde",
    "notReturned": "Kann erst bearbeitet werden, sobald das Auto zurückgegeben wurde"
  },
  "es": {
    "start": "Inicio",
    "end": "Fin",
    "notHandedOver": "solo se puede editar cuando el vehículo haya sido entregado",
    "notReturned": "solo se puede editar cuando el vehículo haya sido devuelto"
  }
}
</i18n>
